import { AlertTriangle } from 'lucide-react'
import { Card, CardContent } from '../ui/card'
import { Badge } from '../ui/badge'
import { type CurrencyAmountProps, type Mint, type SwapStatus } from '../../types/transactions'
import { CryptoIcon, SwapArrow } from '../../components/ui'
import { estimatedFilter } from '../../components/badges/status'
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar'
import { FallbackCompanyIcon } from '../assets/fallback'
import { getNetworkName } from '../../lib/transaction-utils'

function CurrencyCard({
  symbol,
  image,
  amount,
  chain,
  showEstimatedBadge,
}: {
  symbol: string
  image: string
  amount: string
  chain: string
  showEstimatedBadge: boolean
}) {
  return (
    <Card className="p-4 md:p-10 col-span-3 h-full md:min-h-[400px]">
      <CardContent className="p-0">
        <div className="flex flex-col space-x-4">
          <div className="flex items-center gap-2 justify-start md:mt-4">
            <div className="md:w-12 md:h-12 w-8 h-8 flex items-center justify-center">
              <CryptoIcon
                symbol={symbol || ''}
                imageOverride={image || ''}
                className="md:w-12 md:h-12 w-8 h-8"
              />
            </div>
            {showEstimatedBadge && (
              <Badge
                variant="secondary"
                className="bg-destructive/10 text-destructive text-xs leading-none"
              >
                <AlertTriangle className="w-3 h-3 mr-1 md:block hidden" />
                <span>Estimated rate</span>
              </Badge>
            )}
          </div>
          <div className="flex-1 relative">
            <div className="md:text-4xl text-2xl font-bold">{amount}</div>
            <div className="md:text-sm text-xs font-bold">{symbol}</div>
            <div className="md:text-sm text-xs text-muted-foreground">{getNetworkName(chain)}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function CompanyCard({ company, image }: { company: string; image: string }) {
  return (
    <Card className="p-4 md:p-10 col-span-3 h-full md:min-h-[400px]">
      <CardContent className="p-0">
        <div className="flex flex-col items-center">
          <div className=" rounded-md mb-2 overflow-hidden">
            <Avatar className="rounded-sm w-8 h-8 md:w-12 md:h-12">
              <AvatarImage src={image} alt={company} className="rounded-sm" />
              <AvatarFallback className="rounded-sm bg-gray-200">
                <FallbackCompanyIcon className="w-6 h-6 text-gray-500" />
              </AvatarFallback>
            </Avatar>
          </div>
          <div className="font-semibold text-card-text text-center">{company}</div>
        </div>
      </CardContent>
    </Card>
  )
}

export function CurrencyAmountCard({
  amount,
  symbol,
  image,
  chain,
  company,
  companyImage,
}: CurrencyAmountProps) {
  return (
    <div className="grid space-y-4 md:space-y-0 grid-cols-7 2xl:col-span-2">
      <CurrencyCard
        symbol={symbol}
        image={image || ''}
        amount={amount}
        chain={chain}
        showEstimatedBadge={false}
      />
      <div className="mt-4 md:mt-0 flex items-center justify-center">
        <SwapArrow />
      </div>

      <CompanyCard company={company || ''} image={companyImage || ''} />
    </div>
  )
}

interface SwapCurrencyCardsProps {
  fromMint: Mint
  toMint: Mint
  fromAmount: string
  toAmount: string
  status: SwapStatus
}

export function SwapCurrencyCards({
  fromMint,
  toMint,
  fromAmount,
  toAmount,
  status,
}: SwapCurrencyCardsProps) {
  const showEstimatedBadge = estimatedFilter.includes(status)

  return (
    <div className="grid space-y-4 md:space-y-0 grid-cols-7">
      {/* From Card */}
      <CurrencyCard
        symbol={fromMint.symbol || ''}
        image={fromMint.image || ''}
        amount={fromAmount}
        chain={fromMint.chain}
        showEstimatedBadge={false}
      />

      {/* Arrow */}
      <div className="mt-4 md:mt-0 flex items-center justify-center">
        <SwapArrow />
      </div>

      {/* To Card */}
      <CurrencyCard
        symbol={toMint.symbol || ''}
        image={toMint.image || ''}
        amount={toAmount}
        chain={toMint.chain}
        showEstimatedBadge={showEstimatedBadge}
      />
    </div>
  )
}
